import { useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { motion, AnimatePresence } from "framer-motion";
import { Reveal } from "@/components/site/Reveal";
import { scrollToSection } from "@/hooks/useLenis";
import { ArrowRight, Info, Mail } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const BREAKDOWN = [
  { label: "Labour", pct: 0.45 },
  { label: "Materials", pct: 0.4 },
  { label: "Permits & Fees", pct: 0.15 },
];

const SERVICES = [
  { id: "Kitchen", label: "Kitchen", low: 24000, high: 45000 },
  { id: "Bathroom", label: "Bathroom", low: 11000, high: 22000 },
  { id: "Addition", label: "Addition", low: 90000, high: 165000 },
  { id: "Whole Home", label: "Whole Home", low: 60000, high: 140000 },
];

const SIZES = [
  { id: "compact", label: "Compact", note: "Small footprint", factor: 0.78 },
  { id: "standard", label: "Standard", note: "Typical scope", factor: 1.0 },
  { id: "large", label: "Large", note: "Generous scale", factor: 1.42 },
];

const FINISHES = [
  { id: "essential", label: "Essential", note: "Durable, clean", factor: 0.85 },
  { id: "signature", label: "Signature", note: "Elevated materials", factor: 1.0 },
  { id: "bespoke", label: "Bespoke", note: "Custom, high-end", factor: 1.4 },
];

const fmt = (n) =>
  new Intl.NumberFormat("en-CA", {
    style: "currency",
    currency: "CAD",
    maximumFractionDigits: 0,
  }).format(n);

const round500 = (n) => Math.round(n / 500) * 500;

const Chip = ({ active, onClick, testid, label, note }) => (
  <button
    type="button"
    data-testid={testid}
    onClick={onClick}
    className={`flex-1 min-w-[120px] text-left px-5 py-4 border transition-colors duration-300 ${
      active
        ? "bg-[#C05C3F] border-[#C05C3F] text-[#F4F3F0]"
        : "border-white/15 text-[#F4F3F0]/70 hover:border-[#C05C3F]"
    }`}
  >
    <div className="font-mono text-xs uppercase tracking-[0.15em]">{label}</div>
    {note && (
      <div
        className={`mt-1 text-xs font-light ${
          active ? "text-[#F4F3F0]/80" : "text-[#F4F3F0]/40"
        }`}
      >
        {note}
      </div>
    )}
  </button>
);

const Group = ({ label, children }) => (
  <div>
    <span className="font-mono text-[11px] uppercase tracking-[0.2em] text-[#F4F3F0]/50">
      {label}
    </span>
    <div className="mt-3 flex flex-wrap gap-3">{children}</div>
  </div>
);

export const Estimator = () => {
  const [service, setService] = useState(SERVICES[0]);
  const [size, setSize] = useState(SIZES[1]);
  const [finish, setFinish] = useState(FINISHES[1]);
  const [email, setEmail] = useState("");
  const [sending, setSending] = useState(false);

  const { low, high } = useMemo(() => {
    const m = size.factor * finish.factor;
    return {
      low: round500(service.low * m),
      high: round500(service.high * m),
    };
  }, [service, size, finish]);

  const lines = useMemo(
    () =>
      BREAKDOWN.map((b) => ({
        label: b.label,
        low: round500(low * b.pct),
        high: round500(high * b.pct),
      })),
    [low, high]
  );

  const emailEstimate = async () => {
    if (!email || !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email)) {
      toast.error("Please enter a valid email address.");
      return;
    }
    setSending(true);
    try {
      await axios.post(`${API}/estimate/email`, {
        email,
        service: service.label,
        size: size.label,
        finish: finish.label,
        low,
        high,
      });
      toast.success(`Estimate sent to ${email}. Check your inbox.`);
      setEmail("");
    } catch (err) {
      toast.error("Couldn't send the estimate. Please try again.");
    } finally {
      setSending(false);
    }
  };

  const requestQuote = () => {
    window.dispatchEvent(
      new CustomEvent("prefill-quote", {
        detail: { project_type: service.id },
      })
    );
    scrollToSection("#quote");
  };

  return (
    <section
      id="estimate"
      data-testid="estimator-section"
      className="relative border-t border-white/10 bg-[#0d0d0c]"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-8 py-24 sm:py-32 grid grid-cols-1 lg:grid-cols-12 gap-16">
        <div className="lg:col-span-5">
          <div className="lg:sticky lg:top-32">
            <span className="font-mono text-xs uppercase tracking-[0.25em] text-[#C05C3F]">
              / Instant estimate
            </span>
            <h2 className="mt-5 font-serif text-4xl sm:text-5xl lg:text-6xl font-light tracking-tight text-[#F4F3F0] leading-[0.95]">
              Ballpark it in <span className="italic">three taps.</span>
            </h2>
            <p className="mt-6 max-w-sm text-base text-[#F4F3F0]/70 font-light leading-relaxed">
              Pick a service, a project size and a finish level to see a realistic
              price range — no email required. Then send us the details and we'll
              come back with a precise, fixed-scope quote.
            </p>
            <div className="mt-8 flex items-start gap-3 border border-white/10 p-4 bg-[#111110]">
              <Info className="h-4 w-4 text-[#C05C3F] mt-0.5 shrink-0" />
              <p
                data-testid="estimator-disclaimer"
                className="text-xs text-[#F4F3F0]/55 font-light leading-relaxed"
              >
                Estimates are approximate and for guidance only. Your final quote
                depends on site conditions, materials and structural scope.
              </p>
            </div>
          </div>
        </div>

        <div className="lg:col-span-7">
          <Reveal className="space-y-10" y={30}>
            <Group label="Service">
              {SERVICES.map((s) => (
                <Chip
                  key={s.id}
                  testid={`est-service-${s.id.replace(/\s+/g, "-").toLowerCase()}`}
                  active={service.id === s.id}
                  onClick={() => setService(s)}
                  label={s.label}
                />
              ))}
            </Group>

            <Group label="Project size">
              {SIZES.map((s) => (
                <Chip
                  key={s.id}
                  testid={`est-size-${s.id}`}
                  active={size.id === s.id}
                  onClick={() => setSize(s)}
                  label={s.label}
                  note={s.note}
                />
              ))}
            </Group>

            <Group label="Finish level">
              {FINISHES.map((f) => (
                <Chip
                  key={f.id}
                  testid={`est-finish-${f.id}`}
                  active={finish.id === f.id}
                  onClick={() => setFinish(f)}
                  label={f.label}
                  note={f.note}
                />
              ))}
            </Group>

            <div
              data-testid="estimator-result"
              className="border border-[#C05C3F]/40 bg-[#111110] p-8"
            >
              <div className="flex items-center justify-between">
                <span className="font-mono text-[11px] uppercase tracking-[0.2em] text-[#F4F3F0]/50">
                  Estimated range
                </span>
                <span className="font-mono text-[11px] uppercase tracking-[0.2em] text-[#C05C3F]">
                  {service.label} · {size.label} · {finish.label}
                </span>
              </div>

              <div className="mt-4 flex flex-wrap items-baseline gap-x-4 gap-y-1">
                <AnimatePresence mode="popLayout">
                  <motion.span
                    key={`${low}-${high}`}
                    initial={{ opacity: 0, y: 12 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -12 }}
                    transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
                    data-testid="estimator-range"
                    className="font-serif font-light text-[#F4F3F0] text-4xl sm:text-5xl lg:text-6xl leading-none"
                  >
                    {fmt(low)} <span className="text-[#C05C3F]">–</span> {fmt(high)}
                  </motion.span>
                </AnimatePresence>
                <span className="font-mono text-[11px] uppercase tracking-[0.15em] text-[#F4F3F0]/40">
                  approx · CAD
                </span>
              </div>

              <div
                data-testid="estimator-breakdown"
                className="mt-6 border-t border-white/10 pt-5"
              >
                <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#F4F3F0]/40 mb-3">
                  What makes up this estimate
                </div>
                <div className="space-y-2.5">
                  {lines.map((l) => (
                    <div
                      key={l.label}
                      data-testid={`breakdown-${l.label.split(" ")[0].toLowerCase()}`}
                      className="flex items-center justify-between gap-4"
                    >
                      <span className="text-sm font-light text-[#F4F3F0]/70">
                        {l.label}
                      </span>
                      <span className="font-mono text-xs tracking-[0.05em] text-[#F4F3F0]/85 tabular-nums">
                        {fmt(l.low)} – {fmt(l.high)}
                      </span>
                    </div>
                  ))}
                </div>
              </div>

              <button
                data-testid="estimator-request-quote"
                onClick={requestQuote}
                className="group mt-8 w-full sm:w-auto font-mono text-xs uppercase tracking-[0.2em] bg-[#C05C3F] text-[#F4F3F0] px-10 py-5 hover:bg-[#F4F3F0] hover:text-[#111110] transition-colors duration-300 flex items-center justify-center gap-3"
              >
                Request a detailed quote
                <ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition-transform duration-300" />
              </button>

              <div className="mt-6 border-t border-white/10 pt-5">
                <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#F4F3F0]/40 mb-3 flex items-center gap-2">
                  <Mail className="h-3.5 w-3.5 text-[#C05C3F]" />
                  Email this estimate to yourself
                </div>
                <div className="flex flex-col sm:flex-row gap-3">
                  <input
                    data-testid="estimator-email-input"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && emailEstimate()}
                    placeholder="you@email.com"
                    className="flex-1 bg-transparent border-0 border-b border-white/20 py-3 text-base font-light text-[#F4F3F0] placeholder:text-[#F4F3F0]/30 focus:outline-none focus:border-[#C05C3F] transition-colors duration-300"
                  />
                  <button
                    data-testid="estimator-email-send"
                    onClick={emailEstimate}
                    disabled={sending}
                    className="font-mono text-xs uppercase tracking-[0.2em] border border-white/20 text-[#F4F3F0]/80 px-6 py-3 hover:border-[#C05C3F] hover:text-[#F4F3F0] transition-colors duration-300 disabled:opacity-50"
                  >
                    {sending ? "Sending..." : "Email it"}
                  </button>
                </div>
              </div>

              <p className="mt-6 font-mono text-[11px] tracking-[0.1em] text-[#F4F3F0]/50">
                Prefer to talk? Call Residential:{" "}
                <span className="text-[#F4F3F0]/80">905 805 7681</span>
              </p>
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
};
